import type { Editor } from "@tiptap/react";

export type ReplaceOptions = {
    caseSensitive?: boolean;
    wholeWord?: boolean;
};

// Treat anything except letters/digits/underscore as a word boundary.
// Covers Latin, digits, Hebrew, Arabic — sufficient for our caption languages.
const WORD_CHAR = /[A-Za-z0-9_\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;

function isWordBoundary(ch: string | undefined): boolean {
    if (ch == null) return true;
    return !WORD_CHAR.test(ch);
}

type FlatSegment = { flatStart: number; docPos: number; length: number };
type FlatBlock = { text: string; segments: FlatSegment[] };

function buildFlatBlocks(editor: Editor): FlatBlock[] {
    const blocks: FlatBlock[] = [];
    editor.state.doc.forEach((block, blockOffset) => {
        let text = "";
        const segments: FlatSegment[] = [];
        block.descendants((node, relPos) => {
            if (!node.isText) return;
            const t = node.text ?? "";
            segments.push({
                flatStart: text.length,
                docPos: blockOffset + 1 + relPos,
                length: t.length,
            });
            text += t;
        });
        blocks.push({ text, segments });
    });
    return blocks;
}

function flatIndexToDocPos(block: FlatBlock, flatIdx: number): number {
    for (const seg of block.segments) {
        if (flatIdx >= seg.flatStart && flatIdx <= seg.flatStart + seg.length) {
            return seg.docPos + (flatIdx - seg.flatStart);
        }
    }
    const last = block.segments[block.segments.length - 1];
    return last ? last.docPos + last.length : 0;
}

export function countOccurrences(
    editor: Editor,
    needle: string,
    opts: ReplaceOptions = {}
): number {
    if (!needle) return 0;
    return findAllRanges(editor, needle, opts).length;
}

export type MatchRange = { from: number; to: number };

export function findAllRanges(
    editor: Editor,
    needle: string,
    opts: ReplaceOptions = {}
): MatchRange[] {
    const out: MatchRange[] = [];
    if (!needle) return out;
    const { caseSensitive = false, wholeWord = false } = opts;
    const cmp = caseSensitive ? needle : needle.toLowerCase();
    const blocks = buildFlatBlocks(editor);
    for (const block of blocks) {
        const hay = caseSensitive ? block.text : block.text.toLowerCase();
        let idx = 0;
        while ((idx = hay.indexOf(cmp, idx)) !== -1) {
            if (wholeWord) {
                const before = block.text[idx - 1];
                const after = block.text[idx + needle.length];
                if (!isWordBoundary(before) || !isWordBoundary(after)) {
                    idx += needle.length;
                    continue;
                }
            }
            const from = flatIndexToDocPos(block, idx);
            const to = flatIndexToDocPos(block, idx + needle.length);
            out.push({ from, to });
            idx += needle.length;
        }
    }
    return out;
}

// If [from, to) sits inside a single word mark, return that mark so the
// replacement text can inherit it. Otherwise return null.
export function wordMarkForRange(editor: Editor, from: number, to: number) {
    const wordType = editor.schema.marks.word;
    if (!wordType) return null;
    const doc = editor.state.doc;
    const startNode = doc.nodeAt(from);
    const endNode = doc.nodeAt(Math.max(from, to - 1));
    const startMark = startNode?.marks.find((m) => m.type === wordType);
    const endMark = endNode?.marks.find((m) => m.type === wordType);
    if (!startMark || !endMark) return null;
    if (
        startMark.attrs.blockIdx !== endMark.attrs.blockIdx ||
        startMark.attrs.wordIdx !== endMark.attrs.wordIdx
    )
        return null;
    return startMark;
}

export function replaceAll(
    editor: Editor,
    needle: string,
    replacement: string,
    opts: ReplaceOptions = {}
): number {
    if (!needle) return 0;
    const ranges = findAllRanges(editor, needle, opts);
    if (ranges.length === 0) return 0;
    const tr = editor.state.tr;
    for (let i = ranges.length - 1; i >= 0; i--) {
        const { from, to } = ranges[i];
        if (replacement.length > 0) {
            const mark = wordMarkForRange(editor, from, to);
            tr.replaceWith(
                from,
                to,
                editor.schema.text(replacement, mark ? [mark] : null)
            );
        } else {
            tr.delete(from, to);
        }
    }
    editor.view.dispatch(tr);
    return ranges.length;
}
